import { Link } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { SITE } from '@/data/site';
import { categories } from '@/data/categories';

const PRINCIPLES = [
  {
    icon: 'ri-checkbox-circle-line',
    title: '只讲能站得住的内容',
    text: '对不确定的信息保持距离，不把猜测当结论。无法核实的私人传闻，我们不做二次传播。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '讨论事情，不针对个人',
    text: '批评可以指向行为与作品，但不指向人本身。我们拒绝把任何人简化成一个标签。',
  },
  {
    icon: 'ri-lightbulb-line',
    title: '给读者可用的东西',
    text: '每篇文章都尽量回答一个具体问题，让读者读完之后多知道一点什么，而不仅是多看一眼。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新与修正',
    text: '内容会随着事情进展被补充和更新。发现错误时，我们优先修正，而不是悄悄删掉。',
  },
];

export default function AboutPage() {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: `关于我们 - ${SITE.name}`,
      url: `${SITE.domain}/about`,
      inLanguage: 'zh-CN',
      description: `${SITE.name}的定位、内容方向与内容原则介绍。`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE.name,
      alternateName: SITE.subName,
      url: SITE.domain,
      email: SITE.email,
      description: SITE.description,
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`关于我们 - ${SITE.name}是怎么做内容的`}
        description={`了解${SITE.name}的定位、创建初衷、内容方向与内容原则，看看我们希望把"吃瓜"这件事做成什么样子。`}
        keywords="关于吃瓜网,内容原则,娱乐观察,网络文化"
        path="/about"
        schema={schema}
      />

      <div className="max-w-container mx-auto px-4 md:px-8 pt-8">
        <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
      </div>

      {/* 头部 */}
      <section className="max-w-container mx-auto px-4 md:px-8 pt-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.3em] text-primary-600">
              <span className="w-6 h-px bg-primary-400" />
              ABOUT US
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-4xl font-black text-foreground-950 leading-tight">
              我们想把"吃瓜"，做成一件有营养的事
            </h1>
            <p className="mt-5 text-base leading-8 text-foreground-600">
              {SITE.name}（{SITE.subName}）是一个专注于娱乐话题与网络文化观察的中文内容站。
              我们不提供转瞬即逝的碎片情绪，而是用完整、可读的长内容，把正在被讨论的事情讲清楚。
            </p>
            <p className="mt-4 text-base leading-8 text-foreground-600">
              从娱乐行业的运行逻辑，到剧集综艺的类型变化，再到网络热梗的传播路径，
              我们希望每一次阅读都能让你多一点判断，而不只是多一点围观。
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/category/yule-redian"
                className="h-11 px-6 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                看看我们在写什么
              </Link>
              <Link
                to="/contact"
                className="h-11 px-6 inline-flex items-center rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                联系编辑部
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="w-full h-72 md:h-80 rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimal%20editorial%20still%20life%20of%20an%20open%20notebook%2C%20pencil%20and%20a%20bowl%20of%20melon%20slices%20on%20a%20cream%20wooden%20desk%2C%20coral%20and%20bamboo%20green%20accents%2C%20soft%20natural%20daylight%2C%20calm%20thoughtful%20atmosphere%20about%20reading%20and%20observation&width=1000&height=800&seq=chigua-about-01&orientation=squarish"
                alt="吃瓜网编辑部的工作桌面，笔记本与瓜果，象征观察与记录"
                width={1000}
                height={800}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 定位 */}
      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-container mx-auto px-4 md:px-8 py-12 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">
            我们的定位与初衷
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-lg bg-background-50 border border-background-200 p-6">
              <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className="ri-eye-line text-xl" />
              </span>
              <h3 className="mt-4 font-heading font-semibold text-foreground-950">观察者</h3>
              <p className="mt-2 text-sm leading-7 text-foreground-600">
                我们站得稍微远一点，把话题放回它的语境里看，而不是跟着情绪走。
              </p>
            </div>
            <div className="rounded-lg bg-background-50 border border-background-200 p-6">
              <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                <i className="ri-file-text-line text-xl" />
              </span>
              <h3 className="mt-4 font-heading font-semibold text-foreground-950">整理者</h3>
              <p className="mt-2 text-sm leading-7 text-foreground-600">
                把零散的信息梳理成一条清楚的线索，让你少翻十页，也能看懂来龙去脉。
              </p>
            </div>
            <div className="rounded-lg bg-background-50 border border-background-200 p-6">
              <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center">
                <i className="ri-shield-check-line text-xl" />
              </span>
              <h3 className="mt-4 font-heading font-semibold text-foreground-950">守门人</h3>
              <p className="mt-2 text-sm leading-7 text-foreground-600">
                在讨论最热的时候保留一点克制，不把没被证实的说法继续传下去。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 内容方向 */}
      <section className="max-w-container mx-auto px-4 md:px-8 py-12 md:py-16">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">
          我们会写哪四类内容
        </h2>
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group flex gap-5 p-6 rounded-lg border border-background-200 hover:border-primary-300 transition-colors"
            >
              <span className="shrink-0 w-11 h-11 rounded-md bg-background-100 text-primary-600 flex items-center justify-center">
                <i className={`${c.icon} text-2xl`} />
              </span>
              <span>
                <span className="block font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {c.name}
                </span>
                <span className="mt-1.5 block text-sm leading-7 text-foreground-600">
                  {c.description}
                </span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 内容原则 */}
      <section className="bg-accent-50 border-y border-accent-200">
        <div className="max-w-container mx-auto px-4 md:px-8 py-12 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-accent-950">
            我们坚持的四条内容原则
          </h2>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PRINCIPLES.map((p) => (
              <div key={p.title} className="flex gap-4">
                <span className="shrink-0 w-10 h-10 rounded-md bg-accent-500 text-accent-50 flex items-center justify-center">
                  <i className={`${p.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading font-semibold text-accent-950">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-7 text-foreground-700">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 更新方式 + 联系 */}
      <section className="max-w-container mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              内容是怎么更新和修正的
            </h2>
            <p className="mt-4 text-base leading-8 text-foreground-600">
              我们不会为了更新而更新。新文章通常来自一个被反复讨论、又确实值得讲清楚的话题。
              发布之后，如果事情有了新进展，我们会在原文中补充说明，而不是另起一篇制造重复内容。
            </p>
            <p className="mt-4 text-base leading-8 text-foreground-600">
              如果你发现某篇文章里的信息已经过时或有误，欢迎通过下面任意方式告诉我们。
              读者指出问题，对我们来说是最有价值的反馈。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-7">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              联系编辑部
            </h3>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md bg-background-50 flex items-center justify-center text-primary-600">
                  <i className="ri-mail-line text-lg" />
                </span>
                <span className="text-foreground-700">邮箱：{SITE.email}</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md bg-background-50 flex items-center justify-center text-primary-600">
                  <i className="ri-chat-1-line text-lg" />
                </span>
                <Link
                  to="/contact"
                  className="text-foreground-700 hover:text-primary-600 transition-colors"
                >
                  在线留言：点击前往联系页面
                </Link>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md bg-background-50 flex items-center justify-center text-primary-600">
                  <i className="ri-time-line text-lg" />
                </span>
                <span className="text-foreground-700">我们通常在几个工作日内回复</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}